import { map as Rmap, find as Rfind, propEq as RpropEq } from "ramda";
import api from "@/api";
import { JSConn } from "@/utilities/js-conn";

interface Client {
  id: string;
  name: string;
  manager: string;
}

interface WorkOrder {
  id: string;
  name: string;
  description: string;
  status: string;
}

interface ClientField {
  id: string;
  name: string;
  description: string;
  acres: number | string;
  client_name: string;
  client_id?: string;
  commodity_id: string;
  commodity?: string;
  map_url: string;
  count_of_work_orders?: number;
  client: Client;
  work_orders?: WorkOrder[];
}

interface ClientFieldState {
  clientFields: ClientField[];
  clientField: ClientField | {};
}

const emptyClientField: ClientField = {
  id: "",
  acres: 0,
  name: "",
  client_name: "",
  client: {
    id: "",
    name: "",
    manager: "",
  },
  description: "",
  client_id: "",
  commodity_id: "",
  map_url: "",
};

const itemDetails = {
  pluralName: "client_fields",
  singularName: "client_field",
};

const apiParamsBuilder = (jsConn: JSConn) => {
  return {
    id: jsConn.formParams.id,
    client_field: {
      ...jsConn.formParams,
    },
  };
};

const state: ClientFieldState = {
  clientFields: [],
  clientField: {},
};

const mutations = {
  LOAD_CLIENT_FIELDS(state: ClientFieldState, payload: ClientField[]) {
    state.clientFields = payload;
  },
  LOAD_CLIENT_FIELD(state: ClientFieldState, payload: ClientField) {
    state.clientField = payload;
  },
};

const actions = {
  needClientFields(context: any, jsConn: JSConn) {
    jsConn.apiParams = jsConn.formParams;
    return api.getItems(jsConn, itemDetails).then(jsConn => {
      const clientFieldsData = jsConn.response;
      context.commit("LOAD_CLIENT_FIELDS", clientFieldsData);
    });
  },
  needClientField(context: any, jsConn: JSConn) {
    jsConn.apiParams = jsConn.formParams;
    return api.getItem(jsConn, itemDetails).then(jsConn => {
      context.commit("LOAD_CLIENT_FIELD", jsConn.response);
      return Promise.resolve(jsConn);
    });
  },
  createClientField(context: any, jsConn: JSConn) {
    jsConn.apiParams = apiParamsBuilder(jsConn);
    return api
      .createItem(jsConn, itemDetails)
      .then(jsConn => {
        context.commit("LOAD_CLIENT_FIELD", jsConn.response);
        return Promise.resolve(jsConn);
      })
      .catch(jsConn => {
        return Promise.reject(jsConn);
      });
  },
  updateClientField(context: any, jsConn: JSConn) {
    jsConn.apiParams = apiParamsBuilder(jsConn);
    return api
      .updateItem(jsConn, itemDetails)
      .then(jsConn => {
        context.commit("LOAD_CLIENT_FIELD", jsConn.response);
        return Promise.resolve(jsConn);
      })
      .catch(jsConn => {
        return Promise.reject(jsConn);
      });
  },
  deleteClientField(context: any, jsConn: JSConn) {
    jsConn.apiParams = apiParamsBuilder(jsConn);
    return api
      .deleteItem(jsConn, itemDetails)
      .then(jsConn => {
        context.commit("LOAD_CLIENT_FIELD", emptyClientField);
        return Promise.resolve(jsConn);
      })
      .catch(jsConn => {
        return Promise.reject(jsConn);
      });
  },
};

const getters = {
  clientFields: (state: ClientFieldState) => {
    return state.clientFields;
  },
  clientField: (state: ClientFieldState) => {
    return state.clientField;
  },
  clientFieldsForAutocomplete: (state: ClientFieldState) => {
    // map over the items and just return a label and an id
    return Rmap(clientField => {
      return {
        value: clientField.id,
        label: `${clientField.client.name}, ${clientField.client.manager}, ${clientField.name}`,
      };
    }, state.clientFields);
  },
  clientFieldById: (state: ClientFieldState) => (id: string) => {
    if (id) {
      return Rfind(clientField => {
        return RpropEq("id", id, clientField);
      }, state.clientFields);
    } else {
      return emptyClientField;
    }
  },
};

const clientFieldsModule = {
  state,
  mutations,
  actions,
  getters,
};

export default clientFieldsModule;
